import React, { type ReactElement } from 'react';
import { CommonButtonV1 } from 'gemlux-common-components';
import { Box, Dialog, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import _ from 'lodash';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { gemActions, GemVaultType } from '../../../redux/gem/gemSlice';
import { RootState } from '../../../redux/store';
import useEnv from '../../../utils/use-env-hook';
import DeleteIconSVG from '../../../assets/img/delete-icon-new.svg';
import { theme } from '../../../theme/theme';
import { getTitle, Titles } from '../../../utils/general-layout-util';

function DeleteGemModel(): ReactElement {
  const dispatch = useAppDispatch();
  const gemToEdit = useAppSelector((state: RootState) => state.gem.gemToEdit);
  const componentStatus = useAppSelector((state: RootState) => state.gem.componentStatus);

  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const firstPathPart = `/${pathParts[1]}`;
  const title = getTitle(firstPathPart);

  const isLoading = _.get(componentStatus, 'deleteGemLoading', false) as boolean;
  const isModelOpen = _.get(componentStatus, 'isDeleteGemModelOpen', false) as boolean;
  const { baseUrl } = useEnv();

  const handleCloseModel = () => {
    if (!isLoading) dispatch(gemActions.closeDeleteGemModel());
  };

  const handleDeleteGem = () => {
    if (gemToEdit) {
      if (title === Titles.MyGemVault) {
        dispatch(gemActions.deleteGemAsync({ gemId: gemToEdit.gemId, type: GemVaultType.GEM_VAULT }));
      }
      if (title === Titles.MyGemFav) {
        dispatch(gemActions.deleteGemAsync({ gemId: gemToEdit.gemId, type: GemVaultType.MY_GEM_FAV }));
      }
      if (title === Titles.GemBank) {
        dispatch(gemActions.deleteGemAsync({ gemId: gemToEdit.gemId, type: GemVaultType.GEM_BANK }));
      }
    }
  };

  return (
    <Dialog open={isModelOpen} maxWidth="xs" PaperProps={{ sx: { borderRadius: '12px' } }}>
      <Box sx={{ p: '20px', display: 'flex', maxWidth: '300px' }} alignItems="center" flexDirection="column">
        <Box component="img" src={DeleteIconSVG} maxWidth="56px" />
        <Typography variant="title4Bold" textAlign="center" marginTop="20px">
          Delete Gem Record
        </Typography>
        <Typography
          variant="tableRowRegular"
          textAlign="center"
          marginTop="20px"
          color={theme.palette.shades.lightDark}
        >
          Are you sure you want to delete the following gem record?
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-around', width: '100%' }} marginTop="20px">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }} flexDirection="row">
            <Box
              component="img"
              src={`${baseUrl}/${gemToEdit?.appearanceImage}`}
              alt="gem image"
              sx={{
                width: '24px',
                height: '24px',
                objectFit: 'cover',
                borderRadius: 'full',
              }}
            />
            <Typography variant="tableRowRegular" textAlign="center">
              {gemToEdit?.species}
            </Typography>
            <Typography variant="tableRowRegular" textAlign="center">
              {gemToEdit?.color}
            </Typography>
            <Typography variant="tableRowRegular" textAlign="center">
              {gemToEdit?.shape}
            </Typography>
            <Typography variant="tableRowRegular" textAlign="center">
              {gemToEdit?.weight}ct
            </Typography>
          </Box>
        </Box>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }} marginTop="20px">
          <CommonButtonV1
            backgroundColor={theme.palette.shades.errorBrightMain}
            hoverBackgroundColor={theme.palette.error.main}
            variant="contained"
            fullWidth
            loading={isLoading}
            onClick={handleDeleteGem}
          >
            <Typography variant="textBase500" color="HighlightText">
              Yes, delete
            </Typography>
          </CommonButtonV1>
          <CommonButtonV1
            variant="outlined"
            backgroundColor={theme.palette.white.main}
            hoverBackgroundColor={theme.palette.shades.lightError}
            fullWidth
            disabled={isLoading}
            onClick={handleCloseModel}
            sx={{ border: `1px solid ${theme.palette.shades.primaryBrightDark}` }}
          >
            {' '}
            <Typography variant="textBase500" color={theme.palette.shades.buttonOutline}>
              Cancel
            </Typography>
          </CommonButtonV1>
          <Box width="12px" />
        </Box>
      </Box>
    </Dialog>
  );
}

export default DeleteGemModel;
